import type { RefObject } from 'react';
import { useEffect, useState } from 'react';

/**
 * Check if an element is overflown
 * @param element The element to check
 * @returns True if the element is overflown
 * @example
 * ```tsx
 * const element = document.getElementById('my-element');
 * if (isOverflown(element)) {
 *  console.log('Element is overflown');
 * }
 * ```
 */
export function isOverflown(element?: HTMLElement) {
  if (!element) return false;
  return (
    element.scrollHeight > element.clientHeight ||
    element.scrollWidth > element.clientWidth
  );
}

/**
 * React hook to check if an element is overflown
 * It observes element resize and updates the state
 * if the element is overflown.
 * @param ref The ref to the element to check
 * @param watch A value to watch for changes, since the ref is doesn't trigger a re-render
 * @param enabled When false, observers are not attached and state stays false
 * @returns True if the element is overflown
 * @example
 * ```tsx
 * const ref = useRef(null);
 * const isOverflown = useIsOverflown(ref);
 * if (isOverflown) {
 *  console.log('Element is overflown');
 * }
 * ```
 */
export function useIsOverflown(
  ref: RefObject<HTMLElement | null>,
  watch?: unknown,
  enabled = true
) {
  const [isOverflownState, setIsOverflownState] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setIsOverflownState(false);
      return;
    }

    const element = ref.current;
    if (!element) return;

    let raf = 0;
    const measure = (): void => {
      setIsOverflownState(isOverflown(element));
    };

    measure();

    const resizeObserver = new ResizeObserver(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(measure);
    });

    resizeObserver.observe(element);

    return () => {
      cancelAnimationFrame(raf);
      resizeObserver.unobserve(element);
    };
  }, [ref, watch, enabled]);

  return isOverflownState;
}
